import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { faGraduationCap } from '@fortawesome/free-solid-svg-icons/faGraduationCap'
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons/faArrowsRotate'
import { faRightToBracket } from '@fortawesome/free-solid-svg-icons/faRightToBracket'
import { useCallback, useState } from 'react'
import { ScrollView } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Text, View, YStack } from 'tamagui'

import { useOnboarding } from '@contexts/OnboardingContext'
import { Progress } from '@components/Progress'
import { useThemeColor } from '@hooks/useThemeColor'

const steps = [
  {
    icon: faGraduationCap,
    title: 'Boas-vindas à\nÁrea de Membros da HeroSpark',
    description:
      'Este é o app de aluno.\nAcesse seus cursos, aulas e conteúdos exclusivos de onde estiver.',
    btnLabel: 'Seguinte',
  },
  {
    icon: faArrowsRotate,
    title: 'Seu progresso\nsempre sincronizado',
    description:
      'Seu avanço nas aulas é o mesmo no app e no navegador do computador. Estude em qualquer lugar sem perder nada.',
    btnLabel: 'Seguinte',
  },
  {
    icon: faRightToBracket,
    title: 'Faça login\npara começar',
    description:
      'Use o mesmo e-mail e senha que você utiliza no computador para acessar o app.',
    btnLabel: 'Ir para o login',
  },
] as const

type Step = 0 | 1 | 2

export function Onboarding() {
  const { finishOnboarding } = useOnboarding()
  const [step, setStep] = useState<Step>(0)
  const accentColor = useThemeColor('accent')
  const insets = useSafeAreaInsets()

  const handleNext = useCallback(async () => {
    if (step === 2) {
      await finishOnboarding()
      return
    }
    setStep((prev) => (prev + 1) as Step)
  }, [step, finishOnboarding])

  const currentStep = steps[step]
  const progressPercent = ((step + 1) / steps.length) * 100

  return (
    <View
      flex={1}
      backgroundColor="$bg-page"
      paddingHorizontal="$size.spacing-xs"
      paddingTop={insets.top + 16}
      paddingBottom={insets.bottom + 16}
    >
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: 100,
        }}
      >
        <Progress
          value={progressPercent}
          height={10}
          foregroundColor="$accent"
          backgroundColor="$progress-track"
          maxWidth={128}
          alignSelf="center"
        />

        <Text
          fontFamily="$body"
          fontWeight="400"
          fontSize="$font-size-xxs"
          color="$text-placeholder"
          textAlign="center"
          textTransform="uppercase"
          marginTop="$size.spacing-xl"
        >
          Passo {step + 1}/{steps.length}
        </Text>

        <Text
          fontFamily="$heading"
          fontWeight="700"
          fontSize="$font-size-lg"
          color="$text-primary"
          textAlign="center"
          marginTop="$size.spacing-md"
        >
          {currentStep.title}
        </Text>

        <YStack
          alignItems="center"
          justifyContent="center"
          marginVertical="$size.spacing-xl"
          width={140}
          height={140}
          borderRadius={70}
          backgroundColor="$accent-subtle"
        >
          <FontAwesomeIcon
            icon={currentStep.icon}
            size={64}
            color={accentColor}
          />
        </YStack>

        <Text
          fontFamily="$body"
          fontWeight="600"
          fontSize="$font-size-sm"
          color="$text-muted"
          textAlign="center"
          paddingHorizontal="$size.spacing-xs"
        >
          {currentStep.description}
        </Text>
      </ScrollView>

      <View
        backgroundColor="$accent"
        borderRadius={12}
        paddingVertical="$size.spacing-xs"
        alignItems="center"
        justifyContent="center"
        pressStyle={{ opacity: 0.8 }}
        onPress={handleNext}
      >
        <Text
          fontFamily="$heading"
          fontWeight="700"
          fontSize="$font-size-xs"
          color="$text-on-accent"
        >
          {currentStep.btnLabel}
        </Text>
      </View>
    </View>
  )
}
